import { useState, useEffect } from "react";
import { CurrentFocusContainerMemento } from "@/types/memento";
import { detectMementoEvent, getEventColor, getEventLabel, generateMementoSummary } from "@/lib/mementoSummary";
import { formatDistanceToNow, format } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageSquare, Paperclip, Edit2, Pin, Star, Trash2, Bot, FolderOpen, Clock, GitBranch, Save, Eye, Filter, Sparkles, Target, Archive, Plus, FolderUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface MementoTimelineProps {
  mementos: CurrentFocusContainerMemento[];
  onSelectMemento: (memento: CurrentFocusContainerMemento) => void;
  selectedMementoId?: string;
  timeFilter?: 'today' | 'week' | 'all';
  showAutoSaves?: boolean;
  showPinnedOnly?: boolean;
  showFavoritedOnly?: boolean;
  showVersionedOnly?: boolean;
  showDeleted?: boolean;
  showHuman?: boolean;
  showBot?: boolean;
  searchText?: string;
  isActive?: boolean; // Whether this timeline is currently visible
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
}

export const MementoTimeline: React.FC<MementoTimelineProps> = ({
  mementos,
  onSelectMemento,
  selectedMementoId,
  timeFilter = 'all',
  showAutoSaves = false, // Default HIDE auto-saves
  showPinnedOnly = false,
  showFavoritedOnly = false,
  showVersionedOnly = false, // Default SHOW all
  showDeleted = false, // Default HIDE deleted
  showHuman = true, // Default SHOW human
  showBot = true, // Default SHOW bot
  searchText = '', // Default empty search
  isActive = false,
  onLoadMore,
  hasMore = false,
  isLoading = false,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [editedSummary, setEditedSummary] = useState("");
  const [timelineHasFocus, setTimelineHasFocus] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showExactTime, setShowExactTime] = useState(false);
  const [panelHeight, setPanelHeight] = useState(180); // Default min height
  const [isResizing, setIsResizing] = useState(false);

  // Handle resize
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Calculate new size based on mouse position
      const viewingElement = document.querySelector('[data-currently-viewing]');
      if (!viewingElement) return; // Safety check
      
      const rect = viewingElement.getBoundingClientRect();
      const newHeight = e.clientY - rect.top;
      
      if (newHeight >= 180) { // Enforce minimum height
        setPanelHeight(newHeight);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Filter mementos
  const filterByTime = (items: CurrentFocusContainerMemento[]) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    return items.filter(item => {
      const itemDate = new Date(item.created_at);
      if (timeFilter === 'today') return itemDate >= today;
      if (timeFilter === 'week') return itemDate >= weekAgo;
      return true;
    });
  };

  const filterAutoSaves = (items: CurrentFocusContainerMemento[]) => {
    if (showAutoSaves) return items;
    
    return items.filter(item => {
      // Always show pinned/favorited regardless of auto-save status
      if (item.pinned || item.favorited) return true;
      
      // Filter out auto_save and history_load (navigational noise)
      return item.trigger_type !== 'auto_save' && item.trigger_type !== 'history_load';
    });
  };

  const filterDeleted = (items: CurrentFocusContainerMemento[]) => {
    if (showDeleted) return items;
    return items.filter(item => !item.deleted);
  };

  const filterBySource = (items: CurrentFocusContainerMemento[]) => {
    return items.filter(item => {
      // For now, we don't have a created_by field, so we'll assume all are human
      // TODO: Add created_by tracking when bot integration is implemented
      // const isBot = item.created_by === 'bot';
      // const isHuman = !isBot;
      // if (!showBot && isBot) return false;
      // if (!showHuman && isHuman) return false;
      return true; // Keep all for now
    });
  };

  const filterBySearch = (items: CurrentFocusContainerMemento[]) => {
    if (!searchText || searchText.trim() === '') return items;
    
    const search = searchText.toLowerCase();
    return items.filter(item => {
      // Search across ALL text content in the memento
      const title = (item.custom_title || '').toLowerCase();
      const summary = (item.custom_summary || generateMementoSummary(item)).toLowerCase();
      
      // Analysis Panel content
      const currentContent = (item.analysis_current_content || '').toLowerCase();
      const originalContent = (item.analysis_original_content || '').toLowerCase();
      const commandText = (item.analysis_command_text || '').toLowerCase();
      
      // Synthesis Panel content
      const synthesisContent = (item.synthesis_content || '').toLowerCase();
      const synthesisInstructions = (item.synthesis_iteration_instructions || '').toLowerCase();
      
      // Comments (search comment text)
      const commentText = (item.analysis_comments || [])
        .map((c: any) => (c.text || '').toLowerCase())
        .join(' ');
      
      // Attachments (search filenames)
      const attachmentNames = [
        ...(item.analysis_attachments || []),
        ...(item.synthesis_attachments || [])
      ]
        .map((a: any) => (a.name || '').toLowerCase())
        .join(' ');
      
      return title.includes(search) || 
             summary.includes(search) || 
             currentContent.includes(search) ||
             originalContent.includes(search) ||
             commandText.includes(search) ||
             synthesisContent.includes(search) ||
             synthesisInstructions.includes(search) ||
             commentText.includes(search) ||
             attachmentNames.includes(search);
    });
  };

  const filterPinned = (items: CurrentFocusContainerMemento[]) => {
    if (!showPinnedOnly) return items;
    return items.filter(item => item.pinned);
  };

  const filterFavorited = (items: CurrentFocusContainerMemento[]) => {
    if (!showFavoritedOnly) return items;
    return items.filter(item => item.favorited);
  };

  const filterVersioned = (items: CurrentFocusContainerMemento[]) => {
    if (!showVersionedOnly) return items;
    return items.filter(item => item.trigger_type === 'version');
  };

  const filteredMementos = filterVersioned(
    filterFavorited(
      filterPinned(
        filterBySearch(
          filterBySource(
            filterDeleted(
              filterAutoSaves(
                filterByTime(mementos)
              )
            )
          )
        )
      )
    )
  );

  // Sort mementos by time (NEWEST FIRST for timeline)
  const sortedMementos = [...filteredMementos].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  // Duplicate pinned items to show at top AND in chronological order
  const pinnedItems = sortedMementos.filter(m => m.pinned);
  const mementosWithPinnedAtTop = [...pinnedItems, ...sortedMementos];

  const mementosWithEvents = mementosWithPinnedAtTop.map((memento, index) => {
    const eventType = detectMementoEvent(memento);
    const eventColor = getEventColor(eventType);
    const eventLabel = getEventLabel(eventType);

    // Check if this is a pinned duplicate (appears in first pinnedItems.length items)
    const isPinnedDuplicate = index < pinnedItems.length;

    return {
      memento,
      eventType,
      eventColor,
      eventLabel,
      isPinnedDuplicate,
    };
  });

  // Keyboard navigation - only when timeline has focus
  useEffect(() => {
    if (!isActive || !timelineHasFocus) return; // Don't steal focus when not visible or not focused
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) {
        return; // Don't interfere with text editing
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        // Update synchronously for instant feedback
        setSelectedIndex(prev => {
          const next = Math.min(prev + 1, mementosWithEvents.length - 1);
          return next;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        // Update synchronously for instant feedback
        setSelectedIndex(prev => {
          const next = Math.max(prev - 1, 0);
          return next;
        });
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (mementosWithEvents[selectedIndex]) {
          onSelectMemento(mementosWithEvents[selectedIndex].memento);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, mementosWithEvents, onSelectMemento, isActive, timelineHasFocus]);

  if (filteredMementos.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground text-sm">
        No history yet
      </div>
    );
  }

  const selectedMemento = mementosWithEvents[selectedIndex];
  // Handle summary-only mementos (lazy loading)
  const commentCount = selectedMemento.memento.analysis_comments?.length ?? 0;
  const attachmentCount = 
    (selectedMemento.memento.analysis_attachments?.length ?? 0) + 
    (selectedMemento.memento.synthesis_attachments?.length ?? 0);

  // Get title and summary
  // Only use auto-generated if user hasn't set a human value
  const displayTitle = selectedMemento.memento.custom_title_is_human 
    ? selectedMemento.memento.custom_title || 'Untitled'
    : selectedMemento.memento.custom_title || selectedMemento.eventLabel;
  const displaySummary = selectedMemento.memento.custom_summary_is_human
    ? selectedMemento.memento.custom_summary || ''
    : selectedMemento.memento.custom_summary || generateMementoSummary(selectedMemento.memento);
  const truncatedSummary = displaySummary.length > 300 ? displaySummary.substring(0, 300) + '...' : displaySummary;

  const handleSaveTitle = async () => {
    const trimmedTitle = editedTitle.substring(0, 50).trim();
    try {
      await supabase
        .from('current_focus_container_mementos')
        .update({ 
          custom_title: trimmedTitle || null,
          custom_title_is_human: true // Mark as human-generated
        })
        .eq('id', selectedMemento.memento.id);
      
      // Update local state
      selectedMemento.memento.custom_title = trimmedTitle || null;
      selectedMemento.memento.custom_title_is_human = true;
      setIsEditingTitle(false);
      toast.success('Title saved');
    } catch (error) {
      console.error('Failed to save title:', error);
      toast.error('Failed to save title');
    }
  };

  const handleSaveSummary = async () => {
    const trimmedSummary = editedSummary.substring(0, 300).trim();
    try {
      await supabase
        .from('current_focus_container_mementos')
        .update({ 
          custom_summary: trimmedSummary || null,
          custom_summary_is_human: true // Mark as human-generated
        })
        .eq('id', selectedMemento.memento.id);
      
      // Update local state
      selectedMemento.memento.custom_summary = trimmedSummary || null;
      selectedMemento.memento.custom_summary_is_human = true;
      setIsEditingSummary(false);
      toast.success('Summary saved');
    } catch (error) {
      console.error('Failed to save summary:', error);
      toast.error('Failed to save summary');
    }
  };

  const handleTogglePin = async () => {
    const memento = selectedMemento.memento;
    try {
      const newPinned = !memento.pinned;
      await supabase
        .from('current_focus_container_mementos')
        .update({ pinned: newPinned })
        .eq('id', memento.id);

      toast.success(newPinned ? "Pinned" : "Unpinned");
      // Parent component should refetch mementos
    } catch (error) {
      console.error('Failed to toggle pin:', error);
      toast.error("Failed to update pin status");
    }
  };

  const handleToggleFavorite = async () => {
    const memento = selectedMemento.memento;
    try {
      const newFavorited = !memento.favorited;
      await supabase
        .from('current_focus_container_mementos')
        .update({ favorited: newFavorited })
        .eq('id', memento.id);

      toast.success(newFavorited ? "Added to favorites" : "Removed from favorites");
      // Parent component should refetch mementos
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      toast.error("Failed to update favorite status");
    }
  };

  const handleDelete = async () => {
    const memento = selectedMemento.memento;
    try {
      await supabase
        .from('current_focus_container_mementos')
        .update({ deleted: true })
        .eq('id', memento.id);

      toast.success("Deleted");
      setShowDeleteDialog(false);
      // Parent component should refetch mementos
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error("Failed to delete");
    }
  };

  // Detect if this is a bot-generated memento (AI operations)
  const isBotGenerated = (memento: CurrentFocusContainerMemento) => {
    // Auto-saves are bot-initiated
    if (memento.trigger_type === 'auto_save') return true;
    
    // Check if AI command was used (iterate, synthesize, refine, summarize, analyze)
    const commandText = memento.analysis_command_text?.toLowerCase() || '';
    const content = memento.analysis_current_content?.toLowerCase() || '';
    
    // AI commands in command text or content
    const aiKeywords = ['iterate', 'synthesize', 'refine', 'summarize', 'analyze'];
    const hasAICommand = aiKeywords.some(keyword => 
      commandText.includes(keyword) || content.includes(`${keyword}:`) || content.includes(`${keyword} `)
    );
    
    return hasAICommand;
  };

  return (
    <div 
      className="h-full flex flex-col"
      onMouseEnter={() => setTimelineHasFocus(true)}
      onMouseLeave={() => setTimelineHasFocus(false)}
      onClick={() => setTimelineHasFocus(true)}
    >
      {/* Fixed Preview Card at Top - Styled like cards */}
      <div 
        className="px-4 py-3 border-b bg-muted/30 relative" 
        data-currently-viewing
        style={{ height: `${panelHeight}px` }}
      >
        <div className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-2">
          <Eye className="h-3.5 w-3.5" />
          CURRENTLY VIEWING
        </div>
        <Card className={`
          p-3 border-l-4 transition-shadow h-[calc(100%-3rem)] min-h-[140px]
          ${selectedMemento.memento.pinned ? 'border-l-yellow-500 bg-yellow-50/10 dark:bg-yellow-950/5' : ''}
          ${selectedMemento.memento.favorited && !selectedMemento.memento.pinned ? 'border-l-red-500 bg-red-50/10 dark:bg-red-950/5' : ''}
          ${!selectedMemento.memento.pinned && !selectedMemento.memento.favorited ? 'border-l-purple-500 bg-purple-50/5 dark:bg-purple-950/5' : ''}
        `}>
          <div className="flex flex-col gap-2 h-full overflow-hidden">
            {/* Header with icon and event badge - fixed height row */}
            <div className="flex items-start gap-x-2 min-h-[28px] flex-shrink-0">{/* (continues...) */}
              <div className="flex items-center gap-2 flex-1 min-w-0 mr-2">
                {/* Icon based on type */}
                {isBotGenerated(selectedMemento.memento) ? (
                  <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 bg-primary/10">
                    <Bot className="w-3 h-3" />
                  </div>
                ) : (
                  <FolderOpen className="h-4 w-4 text-purple-500 flex-shrink-0" />
                )}
                
                {/* Event type badge */}
                <Badge 
                  variant="outline" 
                  className="text-xs flex-shrink-0 whitespace-nowrap"
                  style={{ 
                    backgroundColor: `${selectedMemento.eventColor}20`,
                    borderColor: selectedMemento.eventColor,
                    color: selectedMemento.eventColor
                  }}
                >
                  {selectedMemento.eventLabel}
                </Badge>
              </div>

              {/* Action buttons - fixed width to prevent overlap */}
              <div className="flex items-center gap-1 flex-shrink-0" style={{ width: '90px' }} onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-7 w-7 p-0 ${selectedMemento.memento.pinned ? 'text-yellow-500' : ''}`}
                  onClick={handleTogglePin}
                  title={selectedMemento.memento.pinned ? "Unpin" : "Pin"}
                >
                  <Pin className={`h-3.5 w-3.5 ${selectedMemento.memento.pinned ? 'fill-current' : ''}`} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-7 w-7 p-0 ${selectedMemento.memento.favorited ? 'text-red-500' : ''}`}
                  onClick={handleToggleFavorite}
                  title={selectedMemento.memento.favorited ? "Unfavorite" : "Favorite"}
                >
                  <Star className={`h-3.5 w-3.5 ${selectedMemento.memento.favorited ? 'fill-current' : ''}`} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteDialog(true);
                  }}
                  title="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {/* 3 icon stats and timestamp - fixed height row */}
            <div className="flex items-center justify-between text-xs h-4 gap-2 flex-shrink-0">
              <div className="flex items-center gap-3 text-muted-foreground flex-shrink-0">
                <div className="flex items-center gap-1">
                  <MessageSquare className="h-3 w-3" />
                  <span>{commentCount}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Paperclip className="h-3 w-3" />
                  <span>{attachmentCount}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span>✏️</span>
                  <span>{selectedMemento.memento.analysis_changes?.length ?? 0}</span>
                </div>
              </div>
              
              {/* Timestamp - right aligned with truncation */}
              <span className="text-muted-foreground truncate text-right">
                {showExactTime 
                  ? format(new Date(selectedMemento.memento.created_at), 'MMM d, yyyy h:mm:ss a')
                  : formatDistanceToNow(new Date(selectedMemento.memento.created_at), { addSuffix: true })
                }
              </span>
            </div>

            {/* Editable title - fixed height */}
            {isEditingTitle ? (
              <div className="flex items-center gap-2 min-h-[28px] flex-shrink-0">
                <Input
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value.substring(0, 50))}
                  className="h-7 text-sm font-semibold"
                  placeholder="Temporary title"
                  autoFocus
                  maxLength={50}
                />
                <Button size="sm" variant="ghost" onClick={handleSaveTitle}>Save</Button>
                <Button size="sm" variant="ghost" onClick={() => setIsEditingTitle(false)}>Cancel</Button>
              </div>
            ) : (
              <div 
                className="text-sm font-semibold flex items-center gap-1 cursor-pointer hover:text-primary group min-h-[20px] flex-shrink-0"
                onClick={() => {
                  setEditedTitle(displayTitle);
                  setIsEditingTitle(true);
                }}
              >
                <span className="truncate">{displayTitle || 'Temporary title'}</span>
                <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity flex-shrink-0" />
              </div>
            )}

            {/* AI Summary - 2 lines with wrap, scrollable container allows overflow */}
            <div className="text-xs font-medium text-foreground/70 border-l-2 border-purple-300 dark:border-purple-700 pl-2 line-clamp-2 flex-shrink-0" style={{ minHeight: '32px', maxHeight: '32px' }}>
              {truncatedSummary}
            </div>
          </div>
        </Card>
        
        {/* Resize Handle */}
        <div
          className="absolute bottom-0 left-0 right-0 h-1 cursor-ns-resize hover:bg-primary/20 active:bg-primary/40 transition-colors group"
          onMouseDown={(e) => {
            e.preventDefault();
            setIsResizing(true);
          }}
        >
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-0.5 bg-border opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this draft?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this draft and all associated data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Timeline List - Shows position in history */}
      <div className="px-4 py-3 border-b bg-background">
        <div className="text-xs font-semibold text-muted-foreground flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Filter className="h-3.5 w-3.5" />
            TIMELINE
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => setShowExactTime(!showExactTime)}
            title={showExactTime ? "Show relative time" : "Show exact time"}
            data-testid="timeline-time-toggle"
          >
            <Clock className="h-3 w-3 mr-1" />
            {showExactTime ? 'Exact' : 'Relative'}
          </Button>
        </div>
      </div>
      
      <ScrollArea className="flex-1" data-testid="timeline">
        <div className="px-4 py-2 space-y-1">
          {mementosWithEvents.map(({ memento, eventType, eventColor, eventLabel, isPinnedDuplicate }, index) => {
            const isSelected = index === selectedIndex;
            const itemCommentCount = memento.analysis_comments?.length ?? 0;
            const itemAttachmentCount = (memento.analysis_attachments?.length ?? 0) + (memento.synthesis_attachments?.length ?? 0);
            const itemChangeCount = memento.analysis_changes?.length ?? 0;

            // Get trigger icon
            const getTriggerIcon = () => {
              const iconClass = "h-3.5 w-3.5 flex-shrink-0";
              switch (memento.trigger_type) {
                case 'new_focus': return <Plus className={iconClass} style={{ color: eventColor }} />;
                case 'side_quest': return <Target className={iconClass} style={{ color: eventColor }} />;
                case 'stash': return <Archive className={iconClass} style={{ color: eventColor }} />;
                case 'version': return <Save className={iconClass} style={{ color: eventColor }} />;
                case 'iterate': return <Sparkles className={iconClass} style={{ color: eventColor }} />;
                case 'history_load': return <FolderUp className={iconClass} style={{ color: eventColor }} />;
                case 'manual_save': return <Save className={iconClass} style={{ color: eventColor }} />;
                case 'auto_save': return <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: eventColor }} />;
                default: return <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: eventColor }} />;
              }
            };

            return (
              <div
                key={isPinnedDuplicate ? `pinned-${memento.id}` : memento.id}
                data-testid="timeline-item"
                data-trigger={memento.trigger_type}
                data-pinned={memento.pinned?.toString()}
                data-favorited={memento.favorited?.toString()}
                className={`
                  flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer
                  transition-colors
                  ${isSelected ? 'bg-accent' : 'hover:bg-accent/50'}
                `}
                onClick={() => setSelectedIndex(index)}
                onDoubleClick={() => onSelectMemento(memento)}
                title="Double-click to load in Current Focus"
              >
                {/* Trigger icon on left */}
                <div className="flex items-center pt-0.5">
                  {getTriggerIcon()}
                </div>
                
                {/* Content area with flexible layout */}
                <div className="flex-1 min-w-0 flex flex-col gap-1">
                  {/* Row 1: Title/Label + Status Icons + Change Counts */}
                  <div className="flex items-start gap-2">
                    {/* Title or Event label */}
                    <div className={`text-xs flex-1 min-w-0 line-clamp-2 ${isSelected ? 'font-medium' : ''}`} data-testid="memento-title">
                      {memento.custom_title || eventLabel}
                    </div>

                    {/* Pin/Favorite indicators only (no trigger types) */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {memento.pinned && (
                        <Pin className="h-3 w-3 text-yellow-500 fill-current" data-testid="pin-icon" />
                      )}
                      {memento.favorited && (
                        <Star className="h-3 w-3 text-red-500 fill-current" data-testid="favorite-icon" />
                      )}
                    </div>

                    {/* Icon counts */}
                    <div className="flex items-center gap-2 text-xs font-mono flex-shrink-0">
                      <div className={`flex items-center gap-0.5 ${itemCommentCount > 0 ? 'text-foreground' : 'text-muted-foreground/30'}`}>
                        <MessageSquare className="h-2.5 w-2.5" />
                        <span className="w-4 text-right">{itemCommentCount}</span>
                      </div>
                      <div className={`flex items-center gap-0.5 ${itemAttachmentCount > 0 ? 'text-foreground' : 'text-muted-foreground/30'}`}>
                        <Paperclip className="h-2.5 w-2.5" />
                        <span className="w-4 text-right">{itemAttachmentCount}</span>
                      </div>
                      <div className={`flex items-center gap-0.5 ${itemChangeCount > 0 ? 'text-foreground' : 'text-muted-foreground/30'}`}>
                        <span className="text-xs">✏️</span>
                        <span className="w-4 text-right">{Math.min(itemChangeCount, 99)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Row 2: Time (wraps here on narrow panel) */}
                  <div className="flex justify-end">
                    <span className="text-xs text-muted-foreground flex-shrink-0 text-right">
                      {showExactTime
                        ? format(new Date(memento.created_at), 'MMM d, h:mm:ss a')
                        : formatDistanceToNow(new Date(memento.created_at), { addSuffix: true })
                      }
                    </span>
                  </div>
                </div>
              </div>
          );
        })}
        
        {/* Load More Button */}
        {hasMore && !isLoading && onLoadMore && (
          <div className="flex justify-center py-4">
            <button
              onClick={onLoadMore}
              className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Load More
            </button>
          </div>
        )}
        
        {/* Loading Indicator */}
        {isLoading && (
          <div className="flex justify-center py-4">
            <div className="text-sm text-muted-foreground">Loading...</div>
          </div>
        )}
      </div>
    </ScrollArea>
  </div>
);
}